import { Box, Grid, Typography } from '@atgfr8/atg-ui-tailwind';
import { LTLOptionsArray } from '@common/commonLists';
import {
  FalveyInsuranceQuote,
  LtlQuoteLineItemViewModel,
  LtlQuoteViewModel,
} from '@store/orders';
import { formatAmount } from '@utils/utils';
import { StackCard } from '@views/order/components/header/StackCard';
import { createSignal, Show } from 'solid-js';

export const CostDetailCard = (props: {
  quote: LtlQuoteViewModel;
  isCustomerCost: boolean;
  insuranceQuote?: Pick<FalveyInsuranceQuote, 'premium' | 'serviceFee'>;
  lineItems: LtlQuoteLineItemViewModel[];
}) => {
  const [accessorialsTotal, setAccessorialsTotal] = createSignal<number>();

  const lineItems = () => props.lineItems;

  const getPositiveCharges = (lineItems: LtlQuoteLineItemViewModel[]) => {
    return lineItems.filter((quote) => {
      return (
        (props.isCustomerCost
          ? (quote.customerRate ?? 0)
          : quote.carrierRate) >= 0 && !LTLOptionsArray.includes(quote.type)
      );
    });
  };

  const getNegativeCharges = (lineItems: LtlQuoteLineItemViewModel[]) => {
    return lineItems.filter((quote) => {
      return (
        (props.isCustomerCost ? (quote.customerRate ?? 0) : quote.carrierRate) <
          0 && !LTLOptionsArray.includes(quote.type)
      );
    });
  };

  const getAccessorialCharges = (lineItems: LtlQuoteLineItemViewModel[]) => {
    let currentTotal = 0;
    const accessorial = lineItems.map((quote) => {
      const rate = props.isCustomerCost
        ? quote.customerRate
        : quote.carrierRate;
      if (LTLOptionsArray.includes(quote.type) && rate !== undefined) {
        currentTotal += rate;
        return quote;
      }
    });
    setAccessorialsTotal(currentTotal);
    return accessorial;
  };

  const calculateTotal = () => {
    const total = props.isCustomerCost
      ? (props.quote.customerTotal ?? 0)
      : props.quote.carrierTotal;
    if (!Boolean(props.insuranceQuote?.premium)) {
      return total;
    }

    const { serviceFee, premium } = props.insuranceQuote ?? {};
    const insurancePremium = premium ?? 0;
    const insuranceServiceFee = Boolean(props.isCustomerCost)
      ? (serviceFee ?? 0)
      : 0;

    return total + insurancePremium + insuranceServiceFee;
  };

  return (
    <Box class="p-4 overflow-hidden">
      <Grid container spacing={2}>
        <Grid item size={12}>
          <StackCard title="Accessorials" content="" titleFont="0.875rem" />
          {getAccessorialCharges(lineItems()).map((quote) => {
            if (quote) {
              return (
                <div class="flex justify-between">
                  <Typography variant="body1">{quote.type}</Typography>
                  <Typography variant="body1">
                    {props.isCustomerCost
                      ? formatAmount(quote.customerRate)
                      : formatAmount(quote.carrierRate)}
                  </Typography>
                </div>
              );
            }
            return null;
          })}
          <Grid item size={12} class="flex justify-between">
            <Typography
              variant="body1"
              sxProps={{ whiteSpace: 'nowrap', fontWeight: '500' }}
            >
              Accessorials Total:
            </Typography>
            <Typography variant="body1">
              {formatAmount(accessorialsTotal())}
            </Typography>
          </Grid>
        </Grid>

        {getPositiveCharges(lineItems()).map((quote) => {
          return (
            <Grid item size={12}>
              <StackCard
                titleFont="0.875rem"
                contentFont="1rem"
                contentWeight="400"
                title={quote.type}
                content={
                  props.isCustomerCost
                    ? formatAmount(quote.customerRate)
                    : formatAmount(quote.carrierRate)
                }
              />
            </Grid>
          );
        })}
        {getNegativeCharges(lineItems()).map((quote) => {
          return (
            <Grid size={12}>
              <StackCard
                titleFont="0.875rem"
                contentFont="1rem"
                contentWeight="400"
                title={quote.type}
                content={
                  props.isCustomerCost
                    ? formatAmount(quote.customerRate)
                    : formatAmount(quote.carrierRate)
                }
              />
            </Grid>
          );
        })}

        <Show
          when={
            Boolean(props.isCustomerCost) &&
            Boolean(props.insuranceQuote?.premium) &&
            (props.insuranceQuote?.serviceFee ?? 0) == 0
          }
        >
          <Grid item size={12}>
            <StackCard
              title="Insurance"
              content={formatAmount(props.insuranceQuote?.premium ?? 0)}
              titleFont="0.875rem"
              contentFont="1rem"
              contentWeight="500"
            />
          </Grid>
        </Show>
        <Show
          when={
            Boolean(props.isCustomerCost) &&
            Boolean(props.insuranceQuote?.premium) &&
            (props.insuranceQuote?.serviceFee ?? 0) > 0
          }
        >
          <Grid item size={12}>
            <StackCard
              title="Insurance & Fee"
              content={formatAmount(
                (props.insuranceQuote?.premium ?? 0) +
                  (props.insuranceQuote?.serviceFee ?? 0),
              )}
              titleFont="0.875rem"
              contentFont="1rem"
              contentWeight="500"
            />
          </Grid>
        </Show>
        <Grid item>
          <StackCard
            title="Total"
            content={formatAmount(calculateTotal())}
            titleFont="0.875rem"
            contentFont="1rem"
            contentWeight="500"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
